import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { PiArrowLeftBold, PiWarning } from 'react-icons/pi';
import { routerConst } from '@ewf/lib/router';
import { Button } from '@ewf/components/Button';
import { Staking } from '@ewf/components/Staking';
import { Tooltip } from '@ewf/components/Tooltip';
import chartUpIcon from '@ewf/assets/icons/chart-up.svg';
import roundedCornerIcon from '@ewf/assets/icons/rounded-corner.svg';
import { GenericChip } from '@ewf/components/Chips/GenericChip';
import { UnsubscriptionDelayChip } from '@ewf/components/Chips/UnsubscriptionDelayChip';
import cloudsImage from '@ewf/assets/images/clouds.png';
import { useConnectionStore } from '@ewf/stores/connection';
import { useRemoteResourcesStore } from '@ewf/stores/remote-resources';
import { useApiServiceStore } from '@ewf/stores/api';
import windTurbineImage from '@ewf/assets/images/wind-turbine-long.png';
import questionMarkIcon from '@ewf/assets/icons/question.svg';
import officialBadge from '@ewf/assets/icons/official-solution-group.svg';
import { WorkerApi } from '@shared/types/api';
import { GENERIC_CHIP_COLOR } from '@shared/types/enums';
import clsx from 'clsx';
import { PromptKYCSignature } from '@ewf/components/InitiateKYC';

type Step =
  | 'confirm'
  | 'success'
  | 'failure';

export const WorkerCardLarge = (props: Props): JSX.Element => {
  const workerApi = useApiServiceStore.getState().api;
  const { workerId, namespace, name, minStakingAmount, maxStakingAmount, status, withdrawalDelay } =
    props;
  const isConnected = useConnectionStore((state) => state.isConnected);
  const [isStakingOpen, setIsStakingOpen] = useState(false);
  const [isStakedState, setIsStakedState] = useState(false);
  const [stakeStatusLoading, setStakeStatusLoading] = useState(true);
  const [isPromptKYCSignatureOpen, setIsPromptKYCSignatureOpen] = useState(false);
  const [isKycEnabled, setIsKycEnabled] = useState(false);
  const [kycStatus, setKycStatus] = useState<string | undefined>(undefined);
  const fetchBlacklistedSolutionGroups = useRemoteResourcesStore(
    (state) => state.fetchBlacklistedSolutionGroups,
  );
  const fetchWorkerCardImages = useRemoteResourcesStore((state) => state.fetchWorkerCardImages);
  const [kycStep, setKycStep] = useState<Step>('confirm');
  const [kycFailureMessage, setKycFailureMessage] = useState<string | undefined>(undefined);

  const stake = () => {
    setIsStakingOpen(true);
  };

  const initiateKYC = async () => {
    setKycStep('confirm');
    setKycFailureMessage(undefined);

    try {
      setIsPromptKYCSignatureOpen(true);
      const EWXAddress = (await workerApi.getLastEWXAccount()).ewxAddress;

      if (EWXAddress) {
        const { signature } = await workerApi.walletSignMessage(EWXAddress);
        const kycApiBaseUrl = await workerApi.kycApiUrl();
        const kycUrl = `${kycApiBaseUrl}?address=${EWXAddress}&signature=${signature}&solutionGroup=${namespace}`;

        window.open(kycUrl, '_blank', 'noopener,noreferrer');
        setKycStep('success');
      } else {
        setKycStep('failure');
        setKycFailureMessage('No EWX address found');
      }
    } catch (error) {
      console.error('Failed to initiate KYC:', error);
      setKycStep('failure');
      
      // Extract error message from various error formats
      let errorMessage = 'An unknown error occurred';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = String(error.message);
      }
      
      setKycFailureMessage(errorMessage);
    }
  };  

  const checkStakingStatus = useCallback(async () => {
    const EWXAddress = (await workerApi.getLastEWXAccount()).ewxAddress;
    if (EWXAddress) {
      const isStaked = await workerApi.isStakingTo(namespace, EWXAddress);
      setIsStakedState(isStaked);
    }
    setStakeStatusLoading(false);
  }, [namespace]);

  const buttonStatus = (() => {
    if (stakeStatusLoading) {
      return 'loading';
    }

    if (isConnected && (kycStatus === 'idle' || kycStatus === 'submitted' || kycStatus === 'rejected' || kycStatus === 'approved')) {
      return 'initiate-kyc';
    }

    if (isStakedState) {
      return 'staked';
    }

    if (status === 'expired') {
      return 'expired';
    }

    if (isConnected) {
      return 'opt-in';
    }

    return 'connect';
  })();

  useEffect(() => {
    checkStakingStatus();
  }, [checkStakingStatus]);

  // Check if KYC is enabled for this solution group
  useEffect(() => {
    const checkKycEnabled = async () => {
      setIsKycEnabled(false);
      setKycStatus(undefined);

      const solutionGroupsWithKyc = await workerApi.getSolutionGroupsWithKyc();
      if (solutionGroupsWithKyc.length > 0) {
        const kycEnabled = solutionGroupsWithKyc.find((kyc) => kyc.solutionGroupNamespace === namespace);
        if (kycEnabled) {
          setIsKycEnabled(true);
          setKycStatus('idle');
        }
      }
    };
    
    checkKycEnabled();
  }, [namespace]);

  // Fetch KYC status and set up polling when wallet is connected and KYC is enabled
  useEffect(() => {
    if (!isConnected || !isKycEnabled) {
      return;
    }

    let interval: NodeJS.Timeout | null = null;

    const fetchKycStatus = async () => {
      const solutionGroupKycStatus = await workerApi.getSolutionGroupKyc(namespace);

      if (solutionGroupKycStatus) {
        setKycStatus(solutionGroupKycStatus.status);
        return solutionGroupKycStatus.status;
      }
      return null;
    };

    // Initial fetch and conditionally start polling
    const initKycPolling = async () => {
      const sgKycStatus = await fetchKycStatus();
      
      // Only start polling if status is not in a final state
      if (sgKycStatus !== 'whitelisted' && sgKycStatus !== 'rejected') {
        interval = setInterval(() => {
          fetchKycStatus();
        }, 30000);
      }
    };

    initKycPolling();

    // Cleanup
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [namespace, isConnected, isKycEnabled]);

  useEffect(() => {
    fetchBlacklistedSolutionGroups();
    fetchWorkerCardImages();
  }, [fetchBlacklistedSolutionGroups, fetchWorkerCardImages]);

  return (
    <>
      <WorkerCardLargeView {...props} buttonStatus={buttonStatus} onStake={stake} initiateKYC={initiateKYC} kycStatus={kycStatus} isPromptKYCSignatureOpen={isPromptKYCSignatureOpen} setIsPromptKYCSignatureOpen={setIsPromptKYCSignatureOpen} kycStep={kycStep} kycFailureMessage={kycFailureMessage} />
      <Staking
        workerId={workerId}
        workerName={name}
        namespace={namespace}
        withdrawalDelay={withdrawalDelay}
        isOpen={isStakingOpen}
        setIsOpen={setIsStakingOpen}
        setIsStaked={setIsStakedState}
        amounts={{
          min: minStakingAmount,
          max: maxStakingAmount,
        }}
      />
    </>
  );
};

export interface Props {
  workerId: WorkerApi.WorkerId;
  workerName: string;
  namespace: string;
  category: string;
  name: string;
  description: string;
  subTitle: string;
  subDescription: string;
  progress: number;
  isInstalled?: boolean;
  isStaked: boolean;
  minStakingAmount?: string;
  maxStakingAmount?: string;
  hasOperatorsAllowlist: boolean;
  status: WorkerApi.SolutionGroupStatus;
  withdrawalDelay: number;
  isOfficial: boolean;
}

export const WorkerCardLargeView = (props: WorkerCardLargeViewProps): JSX.Element => {
  const {
    workerId,
    category,
    name,
    description,
    subTitle,
    subDescription,
    hasOperatorsAllowlist,
    buttonStatus,
    withdrawalDelay,
    onStake,
    isOfficial,
    kycStatus,
    initiateKYC,
    isPromptKYCSignatureOpen,
    setIsPromptKYCSignatureOpen,
    kycStep,
    kycFailureMessage,
  } = props;
  const blacklistedSolutions = useRemoteResourcesStore((state) => state.blacklistedWorkers);
  const { large: workerCardImages } = useRemoteResourcesStore((state) => state.workerCardImages);
  const isBlackListed = blacklistedSolutions.includes(workerId.toLowerCase());
  const isPrivate = hasOperatorsAllowlist && isBlackListed;
  const backgroundImage = workerCardImages[workerId.toLowerCase()] || windTurbineImage;

  const KYC_STATUS = {
    required: {
      color: GENERIC_CHIP_COLOR.Gray,
      label: 'KYC Required',
    },
    submitted: {
      color: GENERIC_CHIP_COLOR.Purple,
      label: 'KYC Submitted',
    },
    approved: {
      color: GENERIC_CHIP_COLOR.Teal,
      label: 'KYC Verified',
    },
    rejected: {
      color: GENERIC_CHIP_COLOR.Red,
      label: 'KYC Rejected',
    },
    whitelisted: {
      color: GENERIC_CHIP_COLOR.White,
      label: 'KYC Whitelisted',
    },
  };

  const renderActionButton = (): JSX.Element => {
    if (buttonStatus === 'loading') {
      return (
        <Button disabled size="small" className="w-[145px] bg-button-gradient">
          Loading...
        </Button>
      );
    }

    if (buttonStatus === 'staked') {
      return (
        <Link to={routerConst.Dashboard}>
          <Button size="small" className="w-[145px] bg-button-gradient">
            Manage
          </Button>
        </Link>
      );
    }

    if (buttonStatus === 'expired') {
      return (
        <Button disabled size="small" className="w-[145px] bg-button-gradient">
          Expired
        </Button>
      );
    }

    if (buttonStatus === 'initiate-kyc') {
      return (
        <Button onClick={initiateKYC} disabled={kycStatus === 'rejected' || kycStatus === 'approved'} size="small" className="w-[145px] bg-button-gradient">
          {kycStatus === 'submitted' ? 'Retry' : 'Initiate KYC'}
        </Button>
      );
    }

    return (
      <Button onClick={onStake} size="small" className="w-[145px] bg-button-gradient">
        {buttonStatus === 'opt-in' ? 'Opt-in' : 'Connect'}
      </Button>
    );
  };

  return (
    <section
      style={{ backgroundImage: `url(${cloudsImage})` }}
      className="h-[calc(100vh-128px)] max-h-[656px] w-full rounded-lg bg-cover bg-no-repeat"
    >
      <div className="relative flex h-full w-full flex-col rounded-lg bg-worker-gradient">
        <div className="absolute left-0 top-0 z-0 h-full w-full rounded-b-lg rounded-t-md bg-cover bg-right-bottom bg-no-repeat" />
        <img
          className="absolute bottom-0 right-0 h-[calc(100vh-128px)] max-h-[656px] rounded-bl-lg rounded-br-lg object-cover"
          src={backgroundImage}
          onError={(e) => (e.currentTarget.src = windTurbineImage)}
          alt="Solution group card"
          referrerPolicy="same-origin"
        />
        <div className="relative mb-6 ml-4 mt-4 flex gap-4">
          <Link
            to={routerConst.Discover}
            className="grid min-h-[32px] w-[32px] place-items-center rounded-full bg-white text-black"
            aria-label="back to discover"
          >
            <PiArrowLeftBold size={16} />
          </Link>

          <Tooltip content={<p>This Solution Group is audited and verified by Energy Web</p>}>
            <div
              className={clsx({
                'grid h-[32px] w-[32px] place-items-center rounded-full bg-gray-90': true,
                hidden: !isOfficial,
              })}
            >
              <img src={officialBadge} alt="official badge" />
            </div>
          </Tooltip>
        </div>
        <Link
          to={routerConst.Dashboard}
          className="absolute right-4 top-4 h-[64px] w-[64px] rounded-lg border border-brand bg-violet-darker hover:bg-brand"
        >
          <span className="relative grid h-full w-full place-content-center">
            <img src={chartUpIcon} width={32} height={32} alt="Chart" />
            <img
              src={roundedCornerIcon}
              width={14}
              height={14}
              alt="Corner"
              className="absolute bottom-1.5 left-1.5"
            />
          </span>
        </Link>
        <div className="ml-4 mt-4 flex flex-grow overflow-y-hidden">
          <div className="via-black-50 flex h-min max-h-full w-3/5 max-w-[310px] flex-col rounded-lg bg-gradient-to-b from-black/60 to-black/30 p-6 shadow-lg backdrop-blur">
            {withdrawalDelay > 0 && (
              <div className="mb-6 flex">
                <UnsubscriptionDelayChip />
                <Tooltip
                  side="bottom"
                  content={
                    <div className="max-w-[120px] p-2">{`Unsubscribe will be effective after ${withdrawalDelay} reward period(s).`}</div>
                  }
                >
                  <div className="ml-2 flex h-[16px] w-[16px] justify-center rounded-full bg-gray-100">
                    <img src={questionMarkIcon} width={11} height={11} alt="more info" />
                  </div>
                </Tooltip>
              </div>
            )}
            { kycStatus && (
              <div className="flex gap-2">
                { (kycStatus === 'idle' || kycStatus === 'submitted') && (
                  <div className="mb-2">
                    <GenericChip label={KYC_STATUS['required']?.label} color={KYC_STATUS['required']?.color}></GenericChip>
                  </div>
                )}
                { kycStatus !== 'idle' && (
                  <div className="mb-2">
                    <GenericChip label={KYC_STATUS[kycStatus as keyof typeof KYC_STATUS]?.label} color={KYC_STATUS[kycStatus as keyof typeof KYC_STATUS]?.color}></GenericChip>
                  </div>
                )}
              </div>
            )}
            <p className="mb-3 text-sm uppercase">{category}</p>
            <h2
              title={workerId}
              className="mb-3 font-primary"
              aria-label="single-solution-group-name"
            >
              {name}
            </h2>
            <p className="-mr-3 mb-6 overflow-y-auto pr-3 text-base">{description}</p>
            <div className="flex flex-col gap-2">
              {isPrivate ? (
                <GenericChip label="PRIVATE" color={GENERIC_CHIP_COLOR.White} />
              ) : (
                renderActionButton()
              )}
            </div>
            { kycStatus === 'rejected' && (
              <div className="mt-4 flex gap-2">
                <PiWarning className="text-red bg-black rounded-full p-1" size={20} />
                <div className="flex flex-col">
                  <p className="text-red text-sm">Submission failed. Please contact support</p>
                  <Link to="https://discord.gg/psraNwqGqp" target="_blank">
                    <p className="text-sm underline">Contact</p>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="mt-8 rounded-lg bg-black/50 p-8 backdrop-blur">
          <h3 className="mb-2 font-bold">{subTitle}</h3>
          <p className="text-font-subtle">{subDescription}</p>
        </div>
      </div>
      <PromptKYCSignature isOpen={isPromptKYCSignatureOpen} onClose={() => setIsPromptKYCSignatureOpen(false)} step={kycStep} kycFailureMessage={kycFailureMessage} />
    </section>
  );
};

export interface WorkerCardLargeViewProps extends Props {
  buttonStatus: 'loading' | 'staked' | 'expired' | 'opt-in' | 'connect' | 'initiate-kyc';
  onStake?: () => void;
  initiateKYC?: () => void;
  kycStatus?: string;
  isPromptKYCSignatureOpen: boolean;
  setIsPromptKYCSignatureOpen: (isOpen: boolean) => void;
  kycStep: Step;
  kycFailureMessage?: string;
}
